import { useMutation, UseMutationResult, useQueryClient } from '@tanstack/react-query';
import { AxiosError } from 'axios';
import { revert_automation_version_url } from '@/utils/urls.ts';
import {
  AUTOMATION_KEY,
  AUTOMATION_LATEST_VERSION_KEY,
  AUTOMATION_VERSIONS_KEY,
} from '@/utils/queryKeys.ts';
import useAutomationId from '@/hooks/useAutomationId.ts';
import { useApiClient } from '@/context/ApiClientContext/useApiClient.ts';
import { AutomationVersion } from '@/types/automations.ts';

type RevertAutomationVersionParams = {
  versionId: string;
};

const useRevertAutomationVersion = (): UseMutationResult<
  AutomationVersion,
  AxiosError,
  RevertAutomationVersionParams
> => {
  const apiClient = useApiClient();
  const queryClient = useQueryClient();
  const automationId = useAutomationId();

  return useMutation({
    mutationKey: ['revertAutomationVersion'],
    mutationFn: async ({ versionId }: RevertAutomationVersionParams) => {
      const response = await apiClient.put(revert_automation_version_url(versionId));

      return response.data;
    },
    onSuccess: async () => {
      //   reload the automation in case the version that was reverted was auto-published
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_KEY(automationId) });
      await queryClient.invalidateQueries({
        queryKey: AUTOMATION_LATEST_VERSION_KEY(automationId),
      });
      await queryClient.invalidateQueries({ queryKey: AUTOMATION_VERSIONS_KEY(automationId) });
    },
  });
};

export default useRevertAutomationVersion;
